import { useId } from 'react'
import { ChevronRight, Dices, Sparkles, Users, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/cn'

interface Step {
  /** Canonical wizard step name — ties the strip to the Roster · Vibe · Results stepper (§6.0). */
  name: string
  /** Host-like microcopy in the Fireside voice (§5.2). */
  title: string
  description: string
  Icon: LucideIcon
}

// Roster → Vibe → Results: the flow is a journey, not a form (design-system §1.7).
const steps: readonly Step[] = [
  {
    name: 'Roster',
    title: 'Gather your crew',
    description: "Pick who's around the fire tonight.",
    Icon: Users,
  },
  {
    name: 'Vibe',
    title: "Set tonight's vibe",
    description: 'Chill, epic, quick, or silly — name the mood.',
    Icon: Sparkles,
  },
  {
    name: 'Results',
    title: 'Get grounded picks',
    description: 'A short, honest shortlist — each with its why.',
    Icon: Dices,
  },
]

interface HowItWorksProps {
  className?: string
}

/**
 * "How it works" strip (landing) — a calm 3-step row that communicates the product
 * and reinforces that the flow is a journey, not a form (design-system §1.7).
 *
 * Stays in the calm register: neutral `surface` cards (matching the Card primitive's
 * resting style), parchment text, and amber used only as the small ember glyph disc
 * (§1.4 "amber is meaning, not paint"). Step identity is carried by the number +
 * glyph + label together, never by colour alone (§1.5). No motion, so it is
 * `prefers-reduced-motion`-safe by construction (§4).
 */
export function HowItWorks({ className }: HowItWorksProps) {
  const headingId = useId()

  return (
    <section aria-labelledby={headingId} className={cn('w-full', className)}>
      <h2 id={headingId} className="text-center font-display text-xl font-semibold text-text-strong">
        How tonight comes together
      </h2>

      <ol role="list" className="mt-6 grid list-none grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-3">
        {steps.map(({ name, title, description, Icon }, index) => (
          <li
            key={name}
            // Mirrors ui/Card.tsx resting style so the strip shares the same surface
            // language; `relative` anchors the journey connector below.
            className="relative flex flex-col rounded-card border border-border bg-surface p-6 shadow-warm-sm"
          >
            <div className="flex items-center justify-between">
              {/* The one warm accent per card: a soft ember disc holding the glyph (§1.4). */}
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-chip bg-ember-500/10 ring-1 ring-ember-400/40">
                <Icon className="h-5 w-5 text-ember-300" strokeWidth={1.75} aria-hidden="true" />
              </span>
              {/* Decorative numeral; order is already conveyed by the <ol> and the label. */}
              <span className="font-display text-3xl leading-none text-text-faint" aria-hidden="true">
                {index + 1}
              </span>
            </div>

            <p className="mt-5 text-xs font-medium tracking-wide text-text-muted">{name}</p>
            <h3 className="mt-1 font-display text-xl leading-snug text-text-strong">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">{description}</p>

            {index < steps.length - 1 && (
              // Subtle connector reinforcing the journey: sits in the gap on the sm+
              // row, hidden in the single-column stack. Decorative (§1.7).
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-1/2 hidden -translate-y-1/2 translate-x-full text-text-faint sm:flex"
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
              </span>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}
